const API_BASE = '/api/prisme/rapports-cyber';

/** Entrée de liste de formulaires incident Servo2 (structure flexible selon l’API) */
export interface IncidentFormListItem {
  id?: string | number;
  id_incident?: string | number;
  _id?: string | number;
  form_id?: string | number;
  nom?: string;
  name?: string;
  title?: string;
  date?: string;
  début?: string;
  created_at?: string;
  incident_number?: string;
  [key: string]: unknown;
}

/** Valeur de date du formulaire pour le tri (début > date > created_at). */
function getFormDateRaw(form: IncidentFormListItem): string {
  const d = form.début ?? form.date ?? form.created_at;
  return d != null ? String(d) : '';
}

/** Trie les formulaires du plus récent au plus vieux. */
export function sortFormulairesByDateDesc(forms: IncidentFormListItem[]): IncidentFormListItem[] {
  return [...forms].sort((a, b) => {
    const da = getFormDateRaw(a);
    const db = getFormDateRaw(b);
    if (!da) return 1;
    if (!db) return -1;
    return da > db ? -1 : da < db ? 1 : 0;
  });
}

/** Retourne le(s) nom(s) client du formulaire (champ client, client_name, clients, etc.). */
function getFormClientNames(form: IncidentFormListItem): string[] {
  const raw =
    (form as Record<string, unknown>).client ??
    (form as Record<string, unknown>).client_name ??
    (form as Record<string, unknown>).clients ??
    (form as Record<string, unknown>).client_s;
  if (raw == null) return [];
  if (typeof raw === 'string') return [raw.trim()].filter(Boolean);
  if (Array.isArray(raw)) {
    return raw.map((x) => (typeof x === 'object' && x !== null
      ? String((x as Record<string, unknown>).name ?? (x as Record<string, unknown>).nom ?? x).trim()
      : String(x).trim())).filter((s) => s && s !== '[object Object]');
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const s = String(o.name ?? o.nom ?? o.label ?? o.libelle ?? '').trim();
    return s ? [s] : [];
  }
  return [String(raw).trim()].filter(Boolean);
}

/** Garde uniquement les formulaires dont le client correspond au nom demandé (insensible à la casse). */
export function filterFormsByClientName(
  forms: IncidentFormListItem[],
  clientName: string
): IncidentFormListItem[] {
  const normalized = clientName.trim().toLowerCase();
  if (!normalized) return forms;
  return forms.filter((form) => {
    const names = getFormClientNames(form);
    return names.some((n) => n.toLowerCase().includes(normalized) || normalized.includes(n.toLowerCase()));
  });
}

/** Retourne un identifiant stable pour un formulaire (clé + appel détail). */
export function getFormId(form: IncidentFormListItem, indexFallback?: number): string | number {
  const raw =
    form.id ??
    form.id_incident ??
    form._id ??
    form.form_id ??
    (form as Record<string, unknown>).id_formulaire ??
    (form as Record<string, unknown>).id_rapport;
  if (raw !== undefined && raw !== null && raw !== '') return raw as string | number;
  if (typeof indexFallback === 'number') return `idx-${indexFallback}`;
  return 'unknown';
}

/** Données complètes d’un formulaire (reflète exactement Servo2) */
export type IncidentFormData = Record<string, unknown>;

/** Normalise un item client (string ou objet avec name/nom) en chaîne affichable. */
function toClientDisplayName(item: unknown): string {
  if (item == null) return '';
  if (typeof item === 'string') return item.trim();
  if (typeof item === 'object' && item !== null) {
    const o = item as Record<string, unknown>;
    const s = (o.name ?? o.nom ?? o.label ?? o.libelle ?? '').toString().trim();
    return s || '';
  }
  return String(item).trim();
}

/** Liste des noms de clients présents dans Servo2 (formulaires incident). */
export async function getServo2ClientNames(): Promise<string[]> {
  const res = await fetch(`${API_BASE}/clients-servo2`, { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Erreur chargement des clients Servo2');
  }
  const data = await res.json();
  const arr = Array.isArray(data) ? data : [];
  return arr.map((item) => toClientDisplayName(item)).filter((s) => s && s !== '[object Object]');
}

export async function getFormulairesByClientName(clientName: string): Promise<IncidentFormListItem[]> {
  const res = await fetch(
    `${API_BASE}/formulaires?clientName=${encodeURIComponent(clientName.trim())}`,
    { credentials: 'include' }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Erreur chargement des formulaires incident');
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Récupère le détail d'un formulaire par id. Passer clientName (ex: SOCAM) pour que l'API
 * retourne le bon formulaire du bon client (évite les mauvaises infos / toujours les mêmes).
 */
export async function getFormulaireById(
  formId: string | number,
  clientName?: string
): Promise<IncidentFormData> {
  let path = `${API_BASE}/formulaire/${encodeURIComponent(String(formId))}`;
  if (clientName && clientName.trim()) {
    path += `?clientName=${encodeURIComponent(clientName.trim())}`;
  }
  const res = await fetch(path, { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Erreur chargement du formulaire');
  }
  return await res.json();
}
